<template>
  <div class="merchant-dashboard-header px-0 my-5">
    <!-- KYC required: blocking popup, same pattern as must_change_password -->
    <div v-if="showKycRequired" class="kyc_required_model">
      <b-modal
        id="kyc-required-modal"
        ref="kyc-required-modal"
        size="md"
        title="KYC Verification Required"
        v-bind:hide-footer="true"
        :no-close-on-backdrop="true"
        :no-close-on-esc="true"
        hide-header-close
      >
        <div class="text-center p-2">
          <p>
            Your merchant account is not KYC verified yet. Please complete your
            KYC details to activate all Amejpay merchant services.
          </p>
          <b-button variant="primary" class="mt-2" @click="goToKycForm">
            Update KYC Details
          </b-button>
        </div>
      </b-modal>
    </div>

    <div v-if="profile && profile.author && profile.author.must_change_password" class="change_password_model">
      <b-modal
          id="change-password-form"
          ref="change-password-form"
          size="md"
          title="Change Password"
          v-bind:hide-footer="true"
          :no-close-on-backdrop="true"
          :no-close-on-esc="true"
          hide-header-close
        >
          <b-form @submit.stop.prevent="submitChangePasswordForm">
            <!-- Success Toast -->
            <b-toast
              id="success-toast"
              variant="success"
              solid
              append-toast
              toaster="b-toaster-top-center"
            >
              <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
                <strong class="mr-auto">
                  <p>Password changed successfully!</p>
                </strong>
              </div>
            </b-toast>

            <!-- Error Toast -->
            <b-toast
              id="error-toast"
              variant="danger"
              solid
              append-toast
              toaster="b-toaster-top-center"
            >
              <div slot="toast-title" class="d-flex flex-grow-1 align-items-baseline">
                <strong class="mr-auto">
                  <div v-if="passwordChangeMessage">
                    <p v-if="passwordChangeMessage.length > 0">{{ passwordChangeMessage }}</p>
                  </div>
                </strong>
              </div>
            </b-toast>

            <!-- Loading State -->
            <div v-if="isLoading" class="text-center">
              <b-spinner label="Loading..."></b-spinner>
              <p class="mt-2">Updating password...</p>
            </div>

            <!-- Form Fields -->
            <div v-else>
              <!-- Current Password Field -->
              <label for="current-password" class="dark-variant">Current Password *</label>
              <b-form-input
                id="current-password"
                v-model="formData.currentPassword"
                type="password"
                placeholder="Enter your current password"
                autocomplete="current-password"
                :class="errors.currentPassword ? 'is-invalid' : ''"
                @blur="validateField('currentPassword')"
              ></b-form-input>
              <span class="text-danger" v-if="errors.currentPassword">
                {{ errors.currentPassword }}
              </span>

              <hr />

              <!-- New Password Field -->
              <label for="new-password" class="dark-variant">New Password *</label>
              <b-form-input
                id="new-password"
                v-model="formData.newPassword"
                type="password"
                placeholder="Enter new password"
                autocomplete="new-password"
                :class="errors.newPassword ? 'is-invalid' : ''"
                @blur="validateField('newPassword')"
              ></b-form-input>
              <span class="text-danger" v-if="errors.newPassword">
                {{ errors.newPassword }}
              </span>

              <!-- Password Requirements Info -->
              <small class="text-muted d-block mt-2">
                Password must be at least 8 characters long and contain uppercase, lowercase, and numbers.
              </small>

              <hr />

              <!-- Confirm Password Field -->
              <label for="confirm-password" class="dark-variant">Confirm Password *</label>
              <b-form-input
                id="confirm-password"
                v-model="formData.confirmPassword"
                type="password"
                placeholder="Confirm your new password"
                autocomplete="new-password"
                :class="errors.confirmPassword ? 'is-invalid' : ''"
                @blur="validateField('confirmPassword')"
              ></b-form-input>
              <span class="text-danger" v-if="errors.confirmPassword">
                {{ errors.confirmPassword }}
              </span>

              <hr />

              <!-- Action Buttons -->
              <div class="text-right">
                <b-button
                  variant="secondary"
                  @click="$bvModal.hide('change-password-form')"
                  class="mr-1"
                  :disabled="isLoading"
                >
                  Cancel
                </b-button>
                <b-button 
                  variant="primary" 
                  type="submit" 
                  class="ml-1"
                  :disabled="isLoading"
                >
                  Update Password
                </b-button>
              </div>
            </div>
          </b-form>
        </b-modal>      
    </div>
    <div class="header-elements">
      <div class="profile" v-b-popover.hover.topright="'Click to view profile'">
        <div class="env-switch mr-3" role="group" aria-label="Environment">
          <button
            type="button"
            class="env-btn"
            :class="merchantEnvironment === 'live' ? 'is-active is-live' : ''"
            @click="switchEnvironment('live')"
          >Live</button>
          <button
            type="button"
            class="env-btn"
            :class="merchantEnvironment === 'sandbox' ? 'is-active is-sandbox' : ''"
            @click="switchEnvironment('sandbox')"
          >Sandbox</button>
        </div>
        <b-dropdown
          size="sm"
          variant="white"
          toggle-class="text-decoration-none"
          no-caret
        >
          <template #button-content>
            Welcome, {{ profile && profile.author ? profile.author.full_name : 'User' }} 
            <img v-if="profile && profile.author && profile.author.image" :src="profile.author.image" />
          </template>
          <b-dropdown-item href="#"><router-link :to="{ name: 'merchant-user-details' }">
            Profile
          </router-link></b-dropdown-item>
          <b-dropdown-item href="#"><router-link :to="{ name: 'merchant-kyc' }">
            {{ kycMenuLabel }}
          </router-link></b-dropdown-item>
          <b-dropdown-item :to="{name: 'logout'}">LogOut</b-dropdown-item>
        </b-dropdown>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from "vuex";

export default {
  name: 'MerchantDashboardHeader',
  data() {
    return {
      mode_switches: "",
      options_switches: [
        {
          text: "Test Mode",
          value: 1,
        },
        {
          text: "Live Mode",
          value: 2,
        },
      ],
      noti: {},
      status: "",
      isLoading: false,
      formData: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      errors: {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      },
      passwordChangeMessage: ''
    };
  },
  computed: {
    ...mapState([
      "successmessage",
      "errormessage",
      "message",
      "profile",
      "merchants",
      "merchantEnvironment",
    ]),
    kycStatus: function () {
      const a = this.profile && this.profile.author;
      return (a && a.kyc_status) ? a.kyc_status : '';
    },
    isKycVerified: function () {
      const a = this.profile && this.profile.author;
      return !!(a && a.is_kyc_verified === true);
    },
    // "pending" = submitted, waiting on the admin. Still editable until verified.
    isKycPending: function () {
      return !this.isKycVerified && this.kycStatus === 'pending';
    },
    // Blocking popup ONLY when nothing has been submitted yet.
    showKycRequired: function () {
      const a = this.profile && this.profile.author;
      if (!a) { return false; }
      if (a.must_change_password) { return false; }
      if (a.is_kyc_verified !== false) { return false; }
      // pending / under review must NOT be blocked - only not_submitted (or empty)
      if (this.kycStatus && this.kycStatus !== 'not_submitted') { return false; }
      return this.$route && this.$route.name !== 'merchant-kyc';
    },
    kycMenuLabel: function () {
      if (this.isKycVerified) { return 'KYC Details'; }
      // pending / submitted / rejected all still need the merchant to act on it
      if (['pending', 'submitted', 'rejected'].indexOf(this.kycStatus) !== -1) { return 'Update KYC'; }
      return 'Complete KYC';
    },
    singleMerchant: function() {
      let merchantObj = "";
      if(this.merchants && this.merchants.length > 0 && this.profile){
        // Fixed: Changed = to === for proper comparison
        merchantObj = this.merchants.find((item) => item.author.id === this.profile.author.id);
      }
      return merchantObj;
    }
  },
  created() {
    this.loadAllData();
  },
  mounted: function () {
    if (this.$store.state.token == "") {
      this.$router.replace("/");
    }
  },
  watch: {
    showKycRequired: function (needed) {
      if (needed) {
        this.$nextTick(() => {
          this.$bvModal.show('kyc-required-modal');
        });
      }
    },
    // ✅ FIXED: Watch for profile changes and show modal when profile loads
    profile: function(newProfile) {
      if (newProfile && newProfile.author && newProfile.author.must_change_password) {
        console.log('✅ Password change required - showing modal');
        this.$nextTick(() => {
          this.$bvModal.show('change-password-form');
        });
      }
    }
  },
  methods: {
    ...mapActions(["listnotifications", "listProfile", "listAllMerchants", "changepassword"]),

    /** Global Live <-> Sandbox switch. Keys, URLs and transactions all follow it. */
    switchEnvironment(env) {
      if (this.merchantEnvironment === env) { return; }
      this.$store.commit("setMerchantEnvironment", env);
    },

    goToKycForm() {
      this.$bvModal.hide('kyc-required-modal');
      if (this.$route.name !== 'merchant-kyc') {
        this.$router.push({ name: 'merchant-kyc' });
      }
    },
    
    async loadAllData() {
      try {
        // Load profile - REQUIRED
        await this.listProfile();
      } catch (error) {
        console.error('Error loading profile:', error);
        // Profile is critical, but show error
        this.$bvToast.toast('Failed to load profile', {
          title: 'Error',
          variant: 'danger',
          solid: true,
          appendToast: true,
          toaster: 'b-toaster-top-center'
        });
      }

      // Load merchants - Optional (may be removed or 403)
      try {
        await this.listAllMerchants();
      } catch (error) {
        console.warn('Error loading merchants (may not be available):', error.response?.status, error.response?.statusText);
        // Don't show error to user - this API may have been removed
      }

      // Load notifications - Optional (requires specific permissions)
      try {
        await this.listnotifications();
        if (this.notifications && this.notifications.length > 0) {
          this.noti = this.notifications[0];
        }
      } catch (error) {
        console.warn('Notifications not available (403/permission):', error.response?.status);
        // User may not have permission to view notifications
        this.noti = {};
      }
    },
    
    validateField(fieldName) {
      switch (fieldName) {
        case 'currentPassword':
          this.validateCurrentPassword();
          break;
        case 'newPassword':
          this.validateNewPassword();
          break;
        case 'confirmPassword':
          this.validateConfirmPassword();
          break;
      }
    },

    validateCurrentPassword() {
      this.errors.currentPassword = '';
      if (!this.formData.currentPassword) {
        this.errors.currentPassword = 'Current password is required';
      }
    },

    validateNewPassword() {
      this.errors.newPassword = '';
      const password = this.formData.newPassword;

      if (!password) {
        this.errors.newPassword = 'New password is required';
        return;
      }

      if (password.length < 8) {
        this.errors.newPassword = 'Password must be at least 8 characters long';
        return;
      }

      if (!/[A-Z]/.test(password)) {
        this.errors.newPassword = 'Password must contain at least one uppercase letter';
        return;
      }

      if (!/[a-z]/.test(password)) {
        this.errors.newPassword = 'Password must contain at least one lowercase letter';
        return;
      }

      if (!/[0-9]/.test(password)) {
        this.errors.newPassword = 'Password must contain at least one number';
        return;
      }

      // Check if new password is same as current password
      if (this.formData.currentPassword === password) {
        this.errors.newPassword = 'New password must be different from current password';
        return;
      }
    },

    validateConfirmPassword() {
      this.errors.confirmPassword = '';
      if (!this.formData.confirmPassword) {
        this.errors.confirmPassword = 'Please confirm your new password';
        return;
      }

      if (this.formData.newPassword !== this.formData.confirmPassword) {
        this.errors.confirmPassword = 'Passwords do not match';
      }
    },

    validateAllFields() {
      this.validateCurrentPassword();
      this.validateNewPassword();
      this.validateConfirmPassword();

      return (
        !this.errors.currentPassword &&
        !this.errors.newPassword &&
        !this.errors.confirmPassword
      );
    },

    async submitChangePasswordForm() {
      // Validate all fields
      if (!this.validateAllFields()) {
        return;
      }

      this.isLoading = true;
      this.passwordChangeMessage = '';

      try {
        // ✅ FIXED: Use Vuex action instead of direct $http call
        await this.$store.dispatch('changepassword', {
          old_password: this.formData.currentPassword,
          new_password: this.formData.newPassword,
          confirm_password: this.formData.confirmPassword
        });

        // Success handling
        this.$bvToast.toast('Password updated successfully', {
          title: 'Success',
          variant: 'success',
          solid: true,
          appendToast: true,
          toaster: 'b-toaster-top-center'
        });

        // Reset form and close modal
        this.resetForm();
        this.isLoading = false;
        
        setTimeout(() => {
          this.$bvModal.hide('change-password-form');
          // Optionally refresh or redirect
          // this.$router.go(0); // Uncomment to refresh page
        }, 1500);

      } catch (error) {
        this.isLoading = false;
        
        // Handle specific error responses
        const errorMsg = error.response?.data?.message || 
                        error.message || 
                        'Failed to change password. Please try again.';
        
        if (error.response?.status === 401) {
          this.passwordChangeMessage = 'Current password is incorrect. Please try again.';
        } else {
          this.passwordChangeMessage = errorMsg;
        }

        this.$bvToast.toast(this.passwordChangeMessage, {
          title: 'Error',
          variant: 'danger',
          solid: true,
          appendToast: true,
          toaster: 'b-toaster-top-center'
        });
        
        console.error('Password change error:', error);
      }
    },

    resetForm() {
      this.formData = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      };
      this.errors = {
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      };
      this.passwordChangeMessage = '';
    }
  }
};
</script>

<style lang="scss" scoped>
.env-switch {
  display: inline-flex;
  border: 1px solid #d8dde3;
  border-radius: 20px;
  overflow: hidden;
  background: #f4f6f8;
  vertical-align: middle;
}
.env-btn {
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 600;
  color: #6c757d;
  padding: 5px 14px;
  cursor: pointer;
  outline: none;
  transition: background 0.15s ease, color 0.15s ease;
}
.env-btn:hover { color: #333; }
.env-btn.is-active { color: #fff; }
.env-btn.is-active.is-live { background: #0066b3; }
.env-btn.is-active.is-sandbox { background: #e08b00; }

.merchant-dashboard-header {
  .change_password_model {
    ::v-deep {
      .modal-header {
        border-bottom: 1px solid #e3e6f0;
      }

      .is-invalid {
        border-color: #dc3545;
      }

      .text-danger {
        font-size: 0.875rem;
        display: block;
        margin-top: 0.25rem;
      }
    }
  }

  .header-elements {
    display: flex;
    justify-content: flex-end;
    align-items: center;

    .profile {
      cursor: pointer;

      img {
        width: 30px;
        height: 30px;
        border-radius: 50%;
        margin-left: 0.5rem;
        vertical-align: middle;
      }
    }
  }
}
</style>